import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, Optional, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { UnifiedAuthService } from '../../services/unified-auth.service';
import { ActionNavComponent } from './action-nav/action-nav.component';
import { ActionUserComponent } from './action-user/action-user.component';
import { ActionNotificationComponent } from './action-notification/action-notification.component';
import { SearchBarComponent } from '../search-bar/search-bar.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AppUpdateService } from '../../services/app-update.service';
import { RoutesService } from '../../../routes.service';
import { PreferencesService } from '../../../preferences.service';
import { HttpClient } from '@angular/common/http';
import { UserLoginResponse } from '../../services/responses/pessoa.response';
import { VoiceHotwordService } from '../../services/voice-hotword.service';
import { AiAgentService } from '../../services/API/ai-agent.service';
import { ActionVoiceComponent } from './action-voice/action-voice.component';
import { OneFooterComponent } from '../one-footer/one-footer.component';
import { OneFooterService } from '../one-footer/one-footer.service';
import { MiniSubheaderComponent } from '../mini-subheader/mini-subheader.component';
import { SubPerfilStruct } from '../../services/structs/pessoa/pessoa.struct';
import { getDefaultLogo } from '../../utils/logo.util';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-menu',
    imports: [
        CommonModule,
        RouterModule,
        SearchBarComponent,
        MatBadgeModule,
        ActionNavComponent,
        ActionUserComponent,
        ActionNotificationComponent,
        MatTooltipModule,
        MiniSubheaderComponent,
        MatIconModule,
        MatButtonModule,
        ActionVoiceComponent,
        OneFooterComponent,
    ],
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuComponent implements OnInit, OnDestroy {
  public readonly footer = inject(OneFooterService);
  @Input() showSearch: boolean = true;
  public envTag: 'DEV' | 'STG' | null = null;
  public showActionUser: boolean = false;
  public showActionNotification: boolean = false;
  public areThereNotifications: boolean = false;
  public empresaLogo: string = '';
  public listSubperfil: SubPerfilStruct[] = [];
  logoValid: boolean = false;
  public isStore: boolean = false;
  public userLoginResponse: UserLoginResponse;
  private subscriptions: Subscription[] = [];
  currentTheme: 'light' | 'dark' = 'light';
  public isListening = false;
  public showActionVoice = false;
  public isAdminContext: boolean = false;

  /** Signal de leitura (OnPush-safe): true quando há atualização disponível. */
  get updateAvailable(): boolean { return this.appUpdateService.updateAvailable(); }

  /** Web Speech API disponível (Chrome/Edge) — gate de renderização do botão de voz. */
  get voiceSuportado(): boolean {
    return typeof window !== 'undefined' &&
      !!((window as any).webkitSpeechRecognition || (window as any).SpeechRecognition);
  }

  /** Botão de voz só aparece no ERP (app.*). Em admin.* ou /pdv retorna false. */
  get isErpContext(): boolean {
    if (typeof window === 'undefined') return false;
    const hostname = window.location.hostname;
    if (hostname.startsWith('admin.')) return false;
    if (hostname.startsWith('app.')) return true;
    return !this.router.url.startsWith('/admin') && !this.router.url.startsWith('/pdv');
  }

  /** Ícone do mic reativo ao estado da assistente (signal lido no template → OnPush-safe). */
  get micIcon(): string {
    if (!this.isListening) return 'mic_off';
    switch (this.aiAgent?.estado()) {
      case 'pensando': return 'hourglass_top';
      case 'falando': return 'volume_up';
      default: return 'graphic_eq';
    }
  }

  get micTooltip(): string {
    if (!this.isListening) return 'Voz — clique para configurar';
    switch (this.aiAgent?.estado()) {
      case 'pensando': return 'Processando…';
      case 'falando': return 'Assistente falando…';
      default: return 'Ouvindo "Oi ERP" — clique para configurar';
    }
  }

  constructor(
    private authService: UnifiedAuthService,
    private router: Router,
    @Optional() private voiceHotwordService: VoiceHotwordService,
    @Optional() public aiAgent: AiAgentService,
    private navbarPreferenceService: PreferencesService,
    private themePreferenceService: PreferencesService,
    @Optional() private routesService: RoutesService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
    private appUpdateService: AppUpdateService,
  ) {}

  ngOnInit(): void {
    if (environment.staging) {
      this.envTag = 'STG';
    } else if (!environment.production) {
      this.envTag = 'DEV';
    } else {
      this.envTag = null;
    }

    this.isAdminContext = this.router.url.startsWith('/admin') ||
      (typeof window !== 'undefined' && window.location.hostname.startsWith('admin.'));

    if (this.voiceHotwordService) {
      this.subscriptions.push(
        this.voiceHotwordService.isListening$.subscribe((status) => {
          this.isListening = status;
          this.cdr.markForCheck();
        })
      );
    }
    this.userLoginResponse = this.authService.getUserInfos();

    if (this.isAdminContext) {
      const response = this.userLoginResponse as any;
      this.empresaLogo = response.distribuidorLogado?.preferenciaDistribuidorStruct?.urlLogoEmpresa || '';
    } else {
      this.empresaLogo =
        this.userLoginResponse.empresaLogada?.preferenciaEmpresaStruct?.urlLogoEmpresa ?? '';
    }

    this.currentTheme = this.themePreferenceService.getTheme();
    this.themePreferenceService.loadCorSistema();
    this.listSubperfil =
      this.userLoginResponse.usuarioPermissionStruct?.listSubPerfilStruct ?? [];
  }

  get logoSrc(): string {
    if (this.isAdminContext) {
      return this.empresaLogo ? this.empresaLogo : '/assets/images/admin.webp';
    }
    return this.empresaLogo ? this.empresaLogo : getDefaultLogo();
  }

  /** Iniciais do nome do usuário (até 2 letras) para o avatar no header */
  get userInitials(): string {
    const name = this.userLoginResponse?.nomeUsuario ?? '';
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return (parts[0]?.[0] ?? '?').toUpperCase();
  }

  changeLogo() {
    if (this.isAdminContext) {
      const response = this.userLoginResponse as any;
      this.empresaLogo = response.distribuidorLogado?.preferenciaDistribuidorStruct?.urlLogoEmpresa || '';
    } else {
      this.empresaLogo =
        this.userLoginResponse.empresaLogada?.preferenciaEmpresaStruct?.urlLogoEmpresa ?? '';
    }
  }

  toggleTheme() {
    this.themePreferenceService.toggleTheme();
    this.currentTheme = this.themePreferenceService.getTheme();
  }

  toggleVoice(): void {
    void this.voiceHotwordService?.alternar();
  }

  /** Abre/fecha o popover de configuração/diagnóstico de voz e atualiza os microfones. */
  openVoiceSettings(): void {
    this.showActionVoice = !this.showActionVoice;
    if (this.showActionVoice) {
      void this.voiceHotwordService?.listarDispositivos();
    }
  }

  clickShowActionUser(action: any) {
    if (action == null) {
      this.showActionUser = !this.showActionUser;
      return;
    }

    this.showActionUser = action;
  }

  toggleNavbar(): void {
    this.navbarPreferenceService.toggleNavbarState();
  }

  redirectPage(nomeSubheader: string): void { }

  clickRedirectHome() {
    if (this.routesService && !this.isAdminContext) {
      this.routesService.smartNavigate(['/home']);
    } else {
      const prefix = this.router.url.startsWith('/admin') ? '/admin' : '';
      this.router.navigate([prefix + '/home']);
    }
  }

  clickShowActionNotification(action: any) {
    if (action == null) {
      this.showActionNotification = !this.showActionNotification;
      return;
    }

    this.showActionNotification = action;
  }

  setAreThereNotifications(areThereNotifications: boolean) {
    this.areThereNotifications = areThereNotifications;
  }

  openUpdateModal(): void {
    this.appUpdateService.showUpdateModal();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
  }
}
